'use client';

import { FC, MouseEvent, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { mediaUrl, type Media } from 'models/Media';
import { mediaQuery } from 'networking/mindfeed/queries/media';
import { MediaImage } from './MediaImage';

// How many images on each side of the current one we download ahead, so
// paging is instant.
const PREFETCH_RADIUS = 2;

interface Props {
  images: Media[];
  // The image on screen, or null when the viewer is closed.
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

// Full-screen image viewer, like the macOS app's lightbox: the photo fitted to
// the window over a dark backdrop, with a close button and, for several
// photos, arrows and a counter. Esc closes, ← and → page, and clicking the
// backdrop closes.
export const ImageViewer: FC<Props> = ({
  images,
  index,
  onIndexChange,
  onClose,
}) => {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const isOpen = index !== null;
  const hasMany = images.length > 1;

  const goTo = (next: number) => {
    if (next >= 0 && next < images.length) onIndexChange(next);
  };

  // ← and → page. Esc is handled by the dialog.
  useEffect(() => {
    if (index === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') goTo(index - 1);
      if (event.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  // Download the neighbours of the current image ahead of time.
  useEffect(() => {
    if (index === null) return;
    images
      .slice(Math.max(0, index - PREFETCH_RADIUS), index + PREFETCH_RADIUS + 1)
      .forEach((image) =>
        queryClient
          .query(mediaQuery(image.id))
          .then((asset) => {
            new Image().src = mediaUrl(asset);
          })
          .catch(() => {}),
      );
  }, [images, index, queryClient]);

  // Clicks on the photo or the buttons don't reach the panel itself; a click
  // on the empty area around them does, and closes the viewer.
  const onPanelClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop transition className={styles.backdrop} />

      <DialogPanel className={styles.panel} onClick={onPanelClick}>
        {index !== null && images[index] && (
          <MediaImage
            mediaId={images[index].id}
            className={classNames(styles.image, hasMany && styles.imageMany)}
          />
        )}

        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className={classNames(styles.button, styles.close)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>

        {hasMany && index !== null && (
          <>
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              aria-label={t('media.previousImage')}
              className={classNames(styles.button, styles.arrow, 'left-5')}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              disabled={index === images.length - 1}
              aria-label={t('media.nextImage')}
              className={classNames(styles.button, styles.arrow, 'right-5')}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <span className={styles.counter}>
              {index + 1} / {images.length}
            </span>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
};

// Always dark, whatever the app theme: it's a photo viewer.
const styles = {
  backdrop: classNames(
    'fixed inset-0 bg-black/85 transition-opacity duration-200',
    'data-closed:opacity-0',
  ),
  panel: 'fixed inset-0 flex items-center justify-center px-4 py-16 sm:px-12',
  image: 'max-h-full max-w-full rounded-lg object-contain',
  // Leave room for the arrows.
  imageMany: 'sm:max-w-[calc(100%-6rem)]',
  button: classNames(
    'absolute flex items-center justify-center rounded-full bg-white/15 text-white',
    'backdrop-blur-md transition hover:bg-white/25 disabled:opacity-35 disabled:hover:bg-white/15',
  ),
  close: 'top-4 right-5 h-9 w-9 text-[15px]',
  arrow: 'top-1/2 h-11 w-11 -translate-y-1/2 text-[17px]',
  counter: classNames(
    'absolute bottom-7 rounded-full bg-white/15 px-3.5 py-1.5 backdrop-blur-md',
    'text-[13px] font-semibold tabular-nums text-white',
  ),
};
